import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  SparePart,
  SparePartsSearchRequest,
  SparePartsSearchResponse,
  SparePartsFilters
} from '../models/mrsearch.models';

@Injectable({
  providedIn: 'root'
})
export class SparePartsService {
  private readonly SPARE_PARTS_ENDPOINT = `${environment.apiBaseURL}/api/spare-parts`;
  private readonly IMAGE_SAS_ENDPOINT = `${environment.apiBaseURL}/api/spare-parts/image-sas`;

  constructor(private http: HttpClient) {}

  /**
   * Search spare parts with filters and query
   * @param request - The spare parts search request object
   * @returns Observable with spare parts search results
   */
  public searchSpareParts(request: SparePartsSearchRequest): Observable<SparePartsSearchResponse> {
    let params = new HttpParams();
    
    if (request.query) {
      params = params.set('query', request.query);
    }
    
    if (request.top) {
      params = params.set('top', request.top.toString());
    }

    if (request.filters) {
      if (request.filters.book_code) {
        params = params.set('book_code', request.filters.book_code);
      }
      if (request.filters.page_reference) {
        params = params.set('page_reference', request.filters.page_reference);
      }
      if (request.filters.part_number) {
        params = params.set('part_number', request.filters.part_number);
      }
    }

    // Debug logging
    console.log('Spare Parts API Request:', {
      url: this.SPARE_PARTS_ENDPOINT,
      params: params.toString()
    });

    return this.http.get<any>(this.SPARE_PARTS_ENDPOINT, { params }).pipe(
      map(data => {
        const results: SparePart[] = (data.results || data.value || []).map((item: any) => ({
          book_code: item.book_code || '',
          page_reference: item.page_reference || '',
          item_id: item.item_id || '',
          part_number: item.part_number || '',
          description: item.description || '',
          quantity: item.quantity || '',
          image_file: item.image_file || '',
          image_sas_url: undefined,
          imageLoading: false
        }));

        return {
          success: true,
          results: results,
          total_count: data.total_count || data['@odata.count'] || results.length
        } as SparePartsSearchResponse;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Spare parts search failed:', error);
        return throwError(() => new Error(`Spare parts search API error! status: ${error.status}, message: ${error.message}`));
      })
    );
  }

  /**
   * Get image SAS URL for a spare part
   * @param partNumber - The part number
   * @param bookCode - The book code
   * @param pageReference - The page reference
   * @returns Observable with the SAS URL
   */
  public getImageSasUrl(partNumber: string, bookCode: string, pageReference: string): Observable<string> {
    const params = new HttpParams()
      .set('part_number', partNumber)
      .set('book_code', bookCode)
      .set('page_reference', pageReference);

    return this.http.get<any>(this.IMAGE_SAS_ENDPOINT, { params }).pipe(
      map(data => data.sas_url || data.url || ''),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to get image SAS URL:', error);
        // Return empty string on error so the image just won't display
        return of('');
      })
    );
  }

  /**
   * Load image SAS URLs for spare parts that have image files
   * @param spareParts - Array of spare parts
   * @returns Observable that completes when all images are loaded
   */
  public loadImagesForParts(spareParts: SparePart[]): Observable<SparePart[]> {
    const partsWithImages = spareParts.filter(part => part.image_file);
    
    if (partsWithImages.length === 0) {
      return of(spareParts);
    }

    // Set loading state for parts with images
    partsWithImages.forEach(part => {
      part.imageLoading = true;
    });

    // Create observables for each part that needs an image
    const imageRequests = partsWithImages.map(part => 
      this.getImageSasUrl(part.part_number, part.book_code, part.page_reference).pipe(
        map(url => {
          part.image_sas_url = url;
          part.imageLoading = false;
          return part;
        }),
        catchError((err) => {
          console.error('Error loading image for part:', part.part_number, err);
          part.imageLoading = false;
          part.image_sas_url = undefined;
          return of(part);
        })
      )
    );

    // Wait for all image requests to complete
    return new Observable(observer => {
      let completed = 0;
      imageRequests.forEach(req => {
        req.subscribe({
          next: () => {
            completed++;
            if (completed === imageRequests.length) {
              observer.next(spareParts);
              observer.complete();
            }
          },
          error: (err) => {
            console.error('Error loading image:', err);
            completed++;
            if (completed === imageRequests.length) {
              observer.next(spareParts);
              observer.complete();
            }
          }
        });
      });
    });
  }
}
